import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  ToggleOn,
  ToggleOff,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  type ChannelAutomationSettings,
  CHANNEL_AUTOMATION_INTERVAL_OPTIONS,
  CHECK_INTERVAL_TOOLTIP,
  MASTER_AUTO_DRAFT_TOOLTIP,
  MASTER_OFF_BANNER_TEXT,
} from './automationTabConstants';

const VerbatimHelpTooltip: React.FC<{ text: string; ariaLabel?: string }> = ({
  text,
  ariaLabel = 'Help',
}) => (
  <Tooltip
    title={
      <Box component="span" sx={{ whiteSpace: 'pre-line', display: 'block', fontSize: '0.75rem' }}>
        {text}
      </Box>
    }
    placement="top"
    arrow
    slotProps={{ tooltip: { sx: { maxWidth: 380 } } }}
  >
    <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} aria-label={ariaLabel}>
      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Tooltip>
);

const MasterToggle: React.FC<{
  enabled: boolean;
  onToggle: () => void;
  disabled?: boolean;
}> = ({ enabled, onToggle, disabled = false }) => {
  const muiTheme = useMuiTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <IconButton
        onClick={onToggle}
        disabled={disabled}
        sx={{ p: 0, '&:hover': { backgroundColor: 'transparent' } }}
        aria-label={enabled ? 'Auto-Draft on' : 'Auto-Draft off'}
      >
        {enabled ? (
          <ToggleOn sx={{ fontSize: 40, color: muiTheme.palette.primary.main }} />
        ) : (
          <ToggleOff sx={{ fontSize: 40, color: muiTheme.palette.action.disabled }} />
        )}
      </IconButton>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 28 }}>
        {enabled ? 'ON' : 'OFF'}
      </Typography>
    </Box>
  );
};

export interface AutomationChannelSettingsSectionProps {
  settings: ChannelAutomationSettings;
  onChange: (settings: ChannelAutomationSettings) => void;
  disabled?: boolean;
}

/**
 * UI-02 — Channel Settings card: master Auto-Draft toggle + check interval dropdown.
 * Reads/writes channel_settings.auto_draft_enabled and auto_draft_interval_minutes.
 */
const AutomationChannelSettingsSection: React.FC<AutomationChannelSettingsSectionProps> = ({
  settings,
  onChange,
  disabled = false,
}) => {
  const muiTheme = useMuiTheme();
  const masterEnabled = settings.auto_draft_enabled;
  const controlsDisabled = disabled || !masterEnabled;

  const handleMasterToggle = () => {
    onChange({
      ...settings,
      auto_draft_enabled: !settings.auto_draft_enabled,
    });
  };

  const handleIntervalChange = (minutes: number) => {
    onChange({
      ...settings,
      auto_draft_interval_minutes: minutes,
    });
  };

  return (
    <Box
      sx={{
        px: 3,
        py: 3,
        backgroundColor: muiTheme.palette.background.paper,
        borderRadius: 2,
        border: `1px solid ${muiTheme.palette.divider}`,
        boxShadow: muiTheme.shadows[1],
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Channel Settings
      </Typography>

      {!masterEnabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {MASTER_OFF_BANNER_TEXT}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, opacity: disabled ? 0.6 : 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 500, minWidth: 100 }}>
          Auto-Draft
        </Typography>
        <MasterToggle enabled={masterEnabled} onToggle={handleMasterToggle} disabled={disabled} />
        <VerbatimHelpTooltip text={MASTER_AUTO_DRAFT_TOOLTIP} ariaLabel="Auto-Draft help" />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <Typography
          variant="body1"
          sx={{ fontWeight: 500, color: masterEnabled ? 'text.primary' : 'text.disabled' }}
        >
          Check every
        </Typography>
        <FormControl size="small" sx={{ minWidth: 72 }} disabled={controlsDisabled}>
          <Select
            value={settings.auto_draft_interval_minutes}
            onChange={(e) => handleIntervalChange(Number(e.target.value))}
            inputProps={{ 'aria-label': 'Check interval minutes' }}
            sx={{ '& .MuiSelect-select': { py: 0.75 } }}
          >
            {CHANNEL_AUTOMATION_INTERVAL_OPTIONS.map((mins) => (
              <MenuItem key={mins} value={mins}>
                {mins}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body1" sx={{ color: masterEnabled ? 'text.primary' : 'text.disabled' }}>
          minutes
        </Typography>
        <VerbatimHelpTooltip text={CHECK_INTERVAL_TOOLTIP} ariaLabel="Check interval help" />
      </Box>
    </Box>
  );
};

export default AutomationChannelSettingsSection;
